import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, api } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import {
  ClipboardCheck, NotebookPen, Keyboard, Lock, CheckCircle2, Circle, Loader2,
  PlayCircle, Trash2, ChevronRight, Snowflake, Wallet, Boxes, ScrollText, FileSpreadsheet,
  AlertTriangle, RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';
import DocumentVerifyPanel from './steps/DocumentVerifyPanel';
import NotebookReconcilePanel from './steps/NotebookReconcilePanel';
import FreezeSnapshotPanel from './steps/FreezeSnapshotPanel';
import CashCountPanel from './steps/CashCountPanel';
import StockCountPanel from './steps/StockCountPanel';
import ReviewFindingsPanel from './steps/ReviewFindingsPanel';
import SignoffReportsPanel from './steps/SignoffReportsPanel';
import CompletenessGate from './CompletenessGate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Phase-1 categories that have a Direct-style Verification Queue.
const VERIFY_CATEGORIES = ['purchase_orders', 'expenses', 'transfers'];
// Categories that ONLY support notebook-reconcile (no document verify queue exists for them).
const RECONCILE_ONLY = ['credits', 'cash_outs', 'digital'];
// Phase-1 categories supported by the Notebook-reconcile panel (all of them).
const RECONCILE_CATEGORIES = ['credits', 'cash_outs', 'expenses', 'purchase_orders', 'transfers', 'digital'];

const PHASE_LABELS = {
  0: 'Setup',
  1: 'Phase 1 · Encode Everything',
  2: 'Phase 2 · Freeze & Count',
  3: 'Phase 3 · Facts & Sign-off',
};

// Short "what this step will do" copy (roadmap for steps not yet built).
const STEP_BLURB = {
  credits: 'Reconcile receivables vs your notebook (or verify documents) — quick back-dated late-encode for anything missing.',
  cash_outs: 'Match cash-outs & advances to records; auto-credit the customer where applicable.',
  expenses: 'Confirm receipts; ensure every expense has its cash-out (orphan-leg detection).',
  purchase_orders: 'Verify received POs against the supplier receipt — 3-way compare with zoom + photo gallery.',
  transfers: 'Reconcile branch transfers sent vs received.',
  digital: 'Ensure every GCash/Maya payment has its reference number.',
  freeze: 'Close the cashier shift, then FREEZE the closing snapshot (expected inventory, cash & credit). No selling during the count.',
  cash_count: 'Physically count cash by denomination — cashier drawer, safe & each digital wallet — vs the frozen expected.',
  stock_count: 'Physically count stock vs the frozen expected. Expected auto-recomputes if you late-encode; your actual count stays frozen.',
  review: 'See findings, smart interpretations & net real variance. Auditor approves the count.',
  signoff: 'Admin decides the resolution; SMS + Telegram alerts go out; role-based reports generate.',
};

const STEP_ICON = {
  freeze: Snowflake, cash_count: Wallet, stock_count: Boxes, review: ScrollText, signoff: FileSpreadsheet,
};

function StepRow({ step, state, active, onClick, locked }) {
  const status = state?.status || 'pending';
  const Icon = locked ? Lock : (status === 'complete' ? CheckCircle2 : (STEP_ICON[step.key] || Circle));
  const color = locked ? 'text-slate-300'
    : status === 'complete' ? 'text-emerald-600'
    : status === 'in_progress' ? 'text-[#1A4D2E]' : 'text-slate-300';
  return (
    <button onClick={onClick} data-testid={`wizard-step-${step.key}`}
      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition-colors ${
        active ? 'bg-[#1A4D2E]/10 border border-[#1A4D2E]/20' : 'hover:bg-slate-50 border border-transparent'}`}>
      <Icon size={16} className={color} />
      <span className={`text-sm flex-1 ${active ? 'font-semibold text-[#1A4D2E]' : locked ? 'text-slate-400' : 'text-slate-600'}`}>{step.title}</span>
      {status === 'complete' && <span className="text-[10px] text-emerald-600 font-bold">DONE</span>}
      {locked && status !== 'complete' && <Lock size={11} className="text-slate-300" />}
    </button>
  );
}

export default function FullAuditWizard() {
  const { branches, currentBranch } = useAuth();
  const [config, setConfig] = useState(null);
  const [session, setSession] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [activeStep, setActiveStep] = useState('setup');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Discard-confirm dialog for the resume banner
  const [discardTarget, setDiscardTarget] = useState(null); // draft object to discard

  // launcher state
  const today = new Date().toISOString().slice(0, 10);
  const [mode, setMode] = useState('full');
  const [branchId, setBranchId] = useState(currentBranch?.id || '');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState(today);
  const [anchorNote, setAnchorNote] = useState('');

  // Fetch smart date suggestion whenever branch or mode changes
  useEffect(() => {
    if (!branchId) return;
    api.get(`${BACKEND_URL}/api/audit/suggested-period`, { params: { branch_id: branchId, audit_type: mode } })
      .then(r => {
        if (r.data?.period_from) setFrom(r.data.period_from);
        if (r.data?.period_to) setTo(r.data.period_to);
        setAnchorNote(r.data?.anchor_reason || '');
      }).catch(() => {});
  }, [branchId, mode]);

  const loadConfig = useCallback(async () => {
    try {
      const [cfg, list] = await Promise.all([
        api.get(`${BACKEND_URL}/api/audit-wizard/config`),
        api.get(`${BACKEND_URL}/api/audit-wizard/sessions?status=draft,in_progress`),
      ]);
      setConfig(cfg.data);
      setDrafts(list.data.sessions || []);
    } catch (e) {
      toast.error('Failed to load wizard');
    }
  }, []);
  useEffect(() => { loadConfig(); }, [loadConfig]);

  const startSession = async () => {
    if (!branchId) { toast.error('Select a branch'); return; }
    setLoading(true);
    try {
      const res = await api.post(`${BACKEND_URL}/api/audit-wizard/sessions`, {
        mode, branch_id: branchId, period_from: from, period_to: to,
      });
      setSession(res.data);
      setActiveStep('setup');
      toast.success('Audit started');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start');
    }
    setLoading(false);
  };

  const resumeSession = async (id) => {
    setLoading(true);
    try {
      const res = await api.get(`${BACKEND_URL}/api/audit-wizard/sessions/${id}`);
      setSession(res.data);
      const firstIncomplete = (config?.steps || []).find(s => res.data.steps[s.key] && res.data.steps[s.key].status !== 'complete');
      setActiveStep(firstIncomplete?.key || 'setup');
    } catch { toast.error('Could not resume'); }
    setLoading(false);
  };

  const discard = async () => {
    if (!session) return;
    setDiscardTarget({ id: session.id, isActive: true });
  };

  const discardDraft = async (id, e) => {
    e.stopPropagation();
    const draft = drafts.find(d => d.id === id);
    if (draft) setDiscardTarget({ id, draft, isActive: false });
  };

  const confirmDiscard = async () => {
    if (!discardTarget) return;
    const { id, isActive } = discardTarget;
    try {
      await api.delete(`${BACKEND_URL}/api/audit-wizard/sessions/${id}`);
      if (isActive) { setSession(null); loadConfig(); }
      else setDrafts(prev => prev.filter(d => d.id !== id));
      toast.success('Audit session discarded');
    } catch { toast.error('Could not discard'); }
    setDiscardTarget(null);
  };

  const setStyle = (cat, val) => {
    setSession(s => ({ ...s, recording_styles: { ...s.recording_styles, [cat]: val } }));
  };

  const saveSetup = async () => {
    setSaving(true);
    try {
      const res = await api.patch(`${BACKEND_URL}/api/audit-wizard/sessions/${session.id}`, {
        recording_styles: session.recording_styles,
        step: 'setup', step_status: 'complete',
      });
      setSession(res.data);
      const steps = config.steps.filter(s => res.data.steps[s.key]);
      const idx = steps.findIndex(s => s.key === 'setup');
      setActiveStep(steps[idx + 1]?.key || 'setup');
      toast.success('Setup saved');
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  // Mark the active step complete and auto-advance to the next.
  const markStepComplete = async (stepKey) => {
    setSaving(true);
    try {
      const res = await api.patch(`${BACKEND_URL}/api/audit-wizard/sessions/${session.id}`, {
        step: stepKey, step_status: 'complete',
      });
      setSession(res.data);
      const steps = config.steps.filter(s => res.data.steps[s.key]);
      const idx = steps.findIndex(s => s.key === stepKey);
      setActiveStep(steps[idx + 1]?.key || stepKey);
      toast.success('Step marked complete');
    } catch { toast.error('Could not update step'); }
    setSaving(false);
  };

  // ── Helper: compute step progress for a draft ─────────────────────────────
  const getDraftProgress = (d) => {
    const stepEntries = Object.values(d.steps || {});
    const completed = stepEntries.filter(s => s.status === 'complete').length;
    const total = stepEntries.length;
    const allStepDefs = config?.steps || STEP_DEFS;
    // Find the first incomplete step key
    const firstIncomplete = allStepDefs.find(s => d.steps[s.key] && d.steps[s.key].status !== 'complete');
    return { completed, total, currentStepTitle: firstIncomplete?.title || 'Setup' };
  };

  // ── Launcher (no active session) ──────────────────────────────────────────
  if (!session) {
    // Most recent draft (if any) shown as primary resume banner
    const primaryDraft = drafts[0] || null;
    const otherDrafts = drafts.slice(1);

    return (
      <div className="space-y-5" data-testid="full-audit-launcher">

        {/* ── Prominent Resume Banner (shown above launcher when drafts exist) ── */}
        {primaryDraft && (
          <div className="rounded-xl border border-[#1A4D2E]/30 bg-[#1A4D2E]/5 px-5 py-4" data-testid="audit-resume-banner">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 min-w-0">
                <RotateCcw size={18} className="text-[#1A4D2E] shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#1A4D2E] leading-snug capitalize">
                    Audit session in progress — {primaryDraft.mode} audit
                    <span className="font-normal text-slate-600 ml-1">
                      · {(branches || []).find(b => b.id === primaryDraft.branch_id)?.name || 'Unknown Branch'}
                    </span>
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Period: {primaryDraft.period_from} → {primaryDraft.period_to}
                    {' · '}Started {(primaryDraft.created_at || '').slice(0, 10)}
                    {config && (() => {
                      const p = getDraftProgress(primaryDraft);
                      return <span className="ml-2 font-medium text-slate-600">· {p.completed}/{p.total} steps complete · Next: {p.currentStepTitle}</span>;
                    })()}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Resume to continue from where you left off, or discard this session to start a new audit.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  size="sm"
                  className="bg-[#1A4D2E] hover:bg-[#14532d] text-white h-8 px-4"
                  onClick={() => resumeSession(primaryDraft.id)}
                  disabled={loading}
                  data-testid={`resume-draft-${primaryDraft.id}`}
                >
                  {loading ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <PlayCircle size={13} className="mr-1.5" />}
                  Resume Audit
                </Button>
                <button
                  onClick={(e) => discardDraft(primaryDraft.id, e)}
                  data-testid={`discard-draft-${primaryDraft.id}`}
                  title="Discard this audit session"
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors border border-slate-200"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            {/* Additional drafts (edge case: multiple sessions) */}
            {otherDrafts.length > 0 && (
              <div className="mt-3 pt-3 border-t border-[#1A4D2E]/10 space-y-1.5">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Other sessions</p>
                {otherDrafts.map(d => (
                  <div key={d.id} data-testid={`draft-row-${d.id}`}
                    className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-white border border-slate-200">
                    <button onClick={() => resumeSession(d.id)} className="flex-1 flex items-center gap-2 text-left">
                      <span className="text-xs font-medium text-slate-700 capitalize">{d.mode} audit · {(branches || []).find(b => b.id === d.branch_id)?.name}</span>
                      <span className="text-[10px] text-slate-400">{d.period_from} → {d.period_to}</span>
                    </button>
                    <button onClick={(e) => discardDraft(d.id, e)} className="text-slate-300 hover:text-red-500 transition-colors p-1">
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Launch New Audit (de-emphasized if a draft exists) ── */}
        <div className={`bg-gradient-to-br from-[#1A4D2E] to-[#14532d] rounded-xl p-6 text-white ${primaryDraft ? 'opacity-75' : ''}`}>
          <div className="flex items-center gap-2 mb-1">
            <ClipboardCheck size={20} />
            <h2 className="text-lg font-bold" style={{ fontFamily: 'Manrope' }}>Full Audit Wizard</h2>
            {primaryDraft && <span className="text-[10px] text-white/50 ml-1">— Start a new audit below (requires discarding the current session first)</span>}
          </div>
          <p className="text-sm text-white/70 max-w-2xl mb-5">
            A guided, step-by-step audit so nothing is forgotten: encode everything → freeze the closing
            snapshot → count → facts. Fact-based, defensible, beginner-friendly.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-white/60 mb-1 uppercase tracking-wide">Mode</label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="w-40 bg-white/10 border-white/20 text-white h-9" data-testid="wizard-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">Full (physical count)</SelectItem>
                  <SelectItem value="partial">Partial (desk review)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-white/60 mb-1 uppercase tracking-wide">Branch</label>
              <Select value={branchId} onValueChange={setBranchId}>
                <SelectTrigger className="w-44 bg-white/10 border-white/20 text-white h-9" data-testid="wizard-branch-select"><SelectValue placeholder="Select branch" /></SelectTrigger>
                <SelectContent>{(branches || []).map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-white/60 mb-1 uppercase tracking-wide">From</label>
              <Input type="date" value={from} onChange={e => setFrom(e.target.value)} className="w-40 bg-white/10 border-white/20 text-white h-9" data-testid="wizard-from" />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-white/60 mb-1 uppercase tracking-wide">To</label>
              <Input type="date" value={to} onChange={e => setTo(e.target.value)} className="w-40 bg-white/10 border-white/20 text-white h-9" data-testid="wizard-to" />
            </div>
            <Button onClick={startSession} disabled={loading || !!primaryDraft} data-testid="wizard-start-btn"
              className="bg-white text-[#1A4D2E] hover:bg-white/90 h-9 px-5 font-semibold ml-auto disabled:opacity-40">
              {loading ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <PlayCircle size={15} className="mr-1.5" />}Start Audit
            </Button>
          </div>
          {anchorNote && (
            <div className="mt-3 flex items-start gap-2 bg-white/10 border border-white/20 rounded-lg px-3 py-2">
              <span className="text-white/60 mt-0.5 shrink-0">&#9432;</span>
              <p className="text-xs text-white/80 leading-relaxed" data-testid="anchor-note">{anchorNote}</p>
            </div>
          )}
        </div>

        {/* Discard Confirm Dialog */}
        <Dialog open={!!discardTarget} onOpenChange={(open) => { if (!open) setDiscardTarget(null); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Discard Audit Session?</DialogTitle>
              <DialogDescription>
                {discardTarget?.isActive
                  ? 'Discard the current active audit session? All progress will be permanently lost.'
                  : `Discard this ${discardTarget?.draft?.mode || ''} audit session for ${
                      (branches || []).find(b => b.id === discardTarget?.draft?.branch_id)?.name || 'the selected branch'
                    }? All recorded steps will be permanently lost.`
                }
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setDiscardTarget(null)}>Keep Session</Button>
              <Button className="bg-red-600 hover:bg-red-700" onClick={confirmDiscard} data-testid="confirm-discard-btn">
                Discard Permanently
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ── Active session (stepper + step panel) ─────────────────────────────────
  const steps = (config?.steps || []).filter(s => session.steps[s.key]);
  const phases = [...new Set(steps.map(s => s.phase))];
  const activeStepDef = steps.find(s => s.key === activeStep);
  const isSetup = activeStep === 'setup';
  const activeCategory = activeStepDef?.category;
  const activeStyle = activeCategory ? (session.recording_styles?.[activeCategory] || 'direct') : null;
  const isReconcileOnly = RECONCILE_ONLY.includes(activeCategory);
  const isDirectVerify = !isReconcileOnly && VERIFY_CATEGORIES.includes(activeCategory) && activeStyle === 'direct';
  const isNotebook = isReconcileOnly || (RECONCILE_CATEGORIES.includes(activeCategory) && activeStyle === 'notebook');
  const isCategoryPanel = isDirectVerify || isNotebook;
  const activeStepDone = session.steps?.[activeStep]?.status === 'complete';

  // ── Completeness gate (Phase 1 must be 6/6 before Phase 2 unlocks) ─────────
  const phase1Steps = steps.filter(s => s.phase === 1);
  const phase1Done = phase1Steps.filter(s => session.steps?.[s.key]?.status === 'complete').length;
  const phase1Complete = phase1Steps.length > 0 && phase1Done === phase1Steps.length;
  const isPhase2Step = activeStepDef?.phase === 2;
  const phase2Locked = isPhase2Step && !phase1Complete;

  return (
    <div className="space-y-4" data-testid="full-audit-active">
      {/* Discard Confirm Dialog (active session) */}
      <Dialog open={!!discardTarget} onOpenChange={(open) => { if (!open) setDiscardTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Discard Audit Session?</DialogTitle>
            <DialogDescription>
              Discard the current active audit session? All recorded steps will be permanently lost and cannot be recovered.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDiscardTarget(null)}>Keep Session</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={confirmDiscard} data-testid="confirm-discard-btn">
              Discard Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Header */}
      <div className="flex items-center justify-between bg-white border border-slate-200 rounded-xl px-4 py-3">
        <div>
          <h2 className="font-bold text-slate-800 capitalize" style={{ fontFamily: 'Manrope' }}>{session.mode} Audit</h2>
          <p className="text-xs text-slate-400">{(branches || []).find(b => b.id === session.branch_id)?.name || session.branch_id} · {session.period_from} → {session.period_to}</p>
        </div>
        <Button variant="ghost" size="sm" onClick={discard} data-testid="wizard-discard-btn" className="text-slate-400 hover:text-red-600">
          <Trash2 size={14} className="mr-1.5" />Discard
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
        {/* Stepper */}
        <div className="bg-white border border-slate-200 rounded-xl p-3 h-fit">
          {phase1Steps.length > 0 && (
            <div className={`mx-1 mb-2 px-3 py-2 rounded-lg border ${phase1Complete ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`} data-testid="stepper-completeness-chip">
              <div className="flex items-center justify-between">
                <span className={`text-[11px] font-bold ${phase1Complete ? 'text-emerald-700' : 'text-amber-700'}`}>Phase 1: {phase1Done}/{phase1Steps.length} encoded</span>
                {phase1Complete && <CheckCircle2 size={13} className="text-emerald-600" />}
              </div>
              <div className="h-1.5 rounded-full bg-white/70 overflow-hidden mt-1">
                <div className={`h-full transition-all ${phase1Complete ? 'bg-emerald-600' : 'bg-amber-500'}`} style={{ width: `${Math.round((phase1Done / phase1Steps.length) * 100)}%` }} />
              </div>
            </div>
          )}
          {phases.map(ph => (
            <div key={ph} className="mb-3 last:mb-0">
              <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 px-3 mb-1">{PHASE_LABELS[ph]}</p>
              {steps.filter(s => s.phase === ph).map(s => (
                <StepRow key={s.key} step={s} state={session.steps[s.key]} active={activeStep === s.key}
                  locked={s.phase === 2 && !phase1Complete}
                  onClick={() => setActiveStep(s.key)} />
              ))}
            </div>
          ))}
        </div>

        {/* Step panel */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 min-h-[360px] flex flex-col">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-lg font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>{activeStepDef?.title}</h3>
            {activeCategory && (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                activeStyle === 'notebook' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-[#1A4D2E]/10 text-[#1A4D2E] border-[#1A4D2E]/20'}`}>
                {activeStyle === 'notebook' ? <NotebookPen size={10} /> : <Keyboard size={10} />}
                {activeStyle === 'notebook' ? 'Notebook' : 'Direct'}
              </span>
            )}
            {activeStepDone && <span className="text-[10px] font-bold text-emerald-600 ml-auto">DONE</span>}
          </div>
          {activeStepDef?.category && (
            <p className="text-xs text-slate-400 mb-3">{STEP_BLURB[activeStep]}</p>
          )}

          {isSetup ? (
            <div className="space-y-5">
              <p className="text-sm text-slate-500">For each record type, tell us how this branch works — do you write it in a <b>notebook first</b> (we'll reconcile against it) or <b>encode it directly</b> (we'll verify the documents)?</p>
              <div className="space-y-2.5">
                {(config.recording_categories || []).map(cat => {
                  const val = session.recording_styles?.[cat.key] || 'direct';
                  return (
                    <div key={cat.key} className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-0">
                      <span className="text-sm font-medium text-slate-700">{cat.label}</span>
                      <div className="flex gap-1.5">
                        <button onClick={() => setStyle(cat.key, 'notebook')} data-testid={`style-${cat.key}-notebook`}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${val === 'notebook' ? 'bg-[#1A4D2E] text-white border-[#1A4D2E]' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}`}>
                          <NotebookPen size={13} />Notebook
                        </button>
                        <button onClick={() => setStyle(cat.key, 'direct')} data-testid={`style-${cat.key}-direct`}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${val === 'direct' ? 'bg-[#1A4D2E] text-white border-[#1A4D2E]' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}`}>
                          <Keyboard size={13} />Direct
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <Button onClick={saveSetup} disabled={saving} data-testid="wizard-save-setup-btn" className="bg-[#1A4D2E] hover:bg-[#14532d] text-white h-10 px-6">
                {saving ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <CheckCircle2 size={15} className="mr-1.5" />}Save &amp; Continue
              </Button>
            </div>
          ) : isCategoryPanel ? (
            <div className="flex-1 flex flex-col">
              <div className="flex-1">
                {isDirectVerify
                  ? <DocumentVerifyPanel session={session} stepCategory={activeCategory} />
                  : <NotebookReconcilePanel session={session} stepCategory={activeCategory} />}
              </div>
              <div className="flex items-center justify-end gap-2 pt-4 mt-4 border-t border-slate-100">
                <Button onClick={() => markStepComplete(activeStep)} disabled={saving} data-testid="wizard-mark-step-complete"
                  className="bg-[#1A4D2E] hover:bg-[#14532d] text-white h-9 px-5">
                  {saving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <CheckCircle2 size={14} className="mr-1.5" />}
                  {activeStepDone ? 'Step complete' : 'Mark step complete'} <ChevronRight size={14} className="ml-1" />
                </Button>
              </div>
            </div>
          ) : phase2Locked ? (
            <CompletenessGate phase1Steps={phase1Steps} sessionSteps={session.steps} onJump={setActiveStep} />
          ) : activeStep === 'freeze' ? (
            <FreezeSnapshotPanel session={session} onFrozen={(updated) => {
              setSession(updated);
              const list = config.steps.filter(s => updated.steps[s.key]);
              const idx = list.findIndex(s => s.key === 'freeze');
              setActiveStep(list[idx + 1]?.key || 'freeze');
            }} />
          ) : activeStep === 'cash_count' ? (
            <CashCountPanel session={session} onJump={setActiveStep} onSaved={(updated) => {
              setSession(updated);
              const list = config.steps.filter(s => updated.steps[s.key]);
              const idx = list.findIndex(s => s.key === 'cash_count');
              setActiveStep(list[idx + 1]?.key || 'cash_count');
            }} />
          ) : activeStep === 'stock_count' ? (
            <StockCountPanel session={session} onJump={setActiveStep} onSaved={(updated) => {
              setSession(updated);
              const list = config.steps.filter(s => updated.steps[s.key]);
              const idx = list.findIndex(s => s.key === 'stock_count');
              setActiveStep(list[idx + 1]?.key || 'stock_count');
            }} />
          ) : activeStep === 'review' ? (
            <ReviewFindingsPanel session={session} onApproved={(updated) => {
              setSession(updated);
              const list = config.steps.filter(s => updated.steps[s.key]);
              const idx = list.findIndex(s => s.key === 'review');
              setActiveStep(list[idx + 1]?.key || 'signoff');
            }} />
          ) : activeStep === 'signoff' ? (
            <SignoffReportsPanel session={session} onJump={setActiveStep} onSignedOff={(updated) => setSession(updated)} />
          ) : (
            <div className="flex flex-col items-center justify-center text-center py-12">
              <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mb-3">
                <Lock size={20} className="text-slate-400" />
              </div>
              <p className="text-sm text-slate-600 max-w-md">{STEP_BLURB[activeStep] || 'This step is part of the guided flow.'}</p>
              <p className="text-xs text-slate-400 mt-3">Building in the next phase — your setup is saved and will carry through.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
